import React from 'react';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import { Box, Button, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { OperatorLifecycleView } from '../domain/safetyCases/safetyCaseDetailTabs';
import type { MeasurementReferencesView } from '../domain/safetyCases/measurementReferencesPresenter';
import {
  formatOverviewWhereThisHappens,
  type OverviewControlView,
  type OverviewFollowUpView,
  type OverviewLatestEvidenceView,
  type OverviewNextSafetyScanView,
  type OverviewNextStepView,
} from '../domain/safetyCases/safetyCaseOverviewPresenter';

const secondaryActionSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
};

function humanize(value: unknown, fallback = 'Not recorded'): string {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function SafetyCaseOverviewPanels(props: {
  operatorLifecycleReady: boolean;
  lifecycleReportId: string | null;
  parsedLifecycle: OperatorLifecycleView | null;
  overviewNextStep: OverviewNextStepView;
  nextSafetyScan: OverviewNextSafetyScanView | null;
  canManageSafetyCases: boolean;
  onDominantCta: () => void;
  openSafetyScanHref: string;
  prepareSignedScanHref: string;
  showPrepareSignedScan: boolean;
  latestEvidence: OverviewLatestEvidenceView | null;
  evidenceImageUrl?: string | null;
  hasActiveDraft: boolean;
  hasSignedScan: boolean;
  previousReportId?: string | null;
  onViewPreviousSafetyScan?: () => void;
  overviewPlace: string;
  scanWindowLabel: string;
  scanRecordsQuiet: boolean;
  onViewSafetyScan: () => void;
  onReviewEvidence: () => void;
  showReviewEvidence: boolean;
  overviewControl: OverviewControlView;
  overviewControlsHref: string;
  onRecordControl: () => void;
  showRecordControlCta: boolean;
  overviewFollowUp: OverviewFollowUpView;
  signedRecordHref?: string | null;
  measurementReferences?: MeasurementReferencesView | null;
  onEstablishInitialBaseline?: () => void;
  onEstablishControlReference?: () => void;
}): React.ReactElement {
  return (
    <Stack spacing={2}>
      {!props.operatorLifecycleReady && props.lifecycleReportId ? (
        <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 3 }} data-testid="safety-case-operator-lifecycle-loading" />
      ) : (
      <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid="safety-case-operator-lifecycle">
        <CardContent sx={{ maxWidth: '46rem' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Status — {humanize(props.parsedLifecycle?.operationalState || 'monitoring')}
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 1.25 }} data-testid="safety-case-operational-checklist">
            <Typography variant="body2">
              {props.parsedLifecycle && props.parsedLifecycle.reviewStatus === 'complete'
                ? `✓ Review complete — ${props.parsedLifecycle.reviewReviewed}/${props.parsedLifecycle.reviewReviewed + props.parsedLifecycle.reviewPending}`
                : props.parsedLifecycle && props.parsedLifecycle.reviewPending > 0
                  ? `Review in progress — ${props.parsedLifecycle.reviewReviewed}/${props.parsedLifecycle.reviewReviewed + props.parsedLifecycle.reviewPending}`
                  : 'Review status is loading…'}
            </Typography>
            <Typography variant="body2">
              {props.parsedLifecycle?.controlRecorded ? '✓ Control recorded' : 'Control not recorded'}
            </Typography>
          </Stack>

          <Box data-testid="safety-case-next-step" sx={{ mt: 2.25 }}>
            {props.overviewNextStep.kind === 'review_complete' ? (
              <Stack spacing={0.75}>
                <Typography
                  component="p"
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.0625rem',
                    lineHeight: 1.35,
                    letterSpacing: '0.01em',
                    color: 'text.primary',
                  }}
                >
                  {props.overviewNextStep.line}
                </Typography>
                {props.overviewNextStep.detail ? (
                  <Typography variant="body2" color="text.secondary">
                    {props.overviewNextStep.detail}
                  </Typography>
                ) : null}
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <ArrowForwardOutlined
                  aria-hidden
                  sx={{ fontSize: 20, mt: 0.35, color: 'text.primary' }}
                />
                <Typography
                  component="p"
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.0625rem',
                    lineHeight: 1.35,
                    letterSpacing: '0.01em',
                    color: 'text.primary',
                  }}
                >
                  {props.overviewNextStep.line}
                </Typography>
              </Stack>
            )}
          </Box>

          {props.overviewNextStep.kind === 'review_complete' ? (
            <Box data-testid="safety-case-system-status" sx={{ mt: 2.25 }}>
              <Typography
                component="p"
                sx={{
                  fontWeight: 800,
                  fontSize: '0.8125rem',
                  letterSpacing: '0.04em',
                  color: 'text.secondary',
                }}
              >
                {props.overviewNextStep.systemStatusHeadline}
              </Typography>
              {props.overviewNextStep.systemStatusDetail ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {props.overviewNextStep.systemStatusDetail}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {props.nextSafetyScan ? (
            <Box data-testid="safety-case-next-safety-scan" sx={{ mt: 2.25 }}>
              <Typography
                component="p"
                sx={{
                  fontWeight: 800,
                  fontSize: '0.8125rem',
                  letterSpacing: '0.04em',
                  color: 'text.secondary',
                }}
              >
                {props.nextSafetyScan.headline}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
                {props.nextSafetyScan.sentence}
              </Typography>
              {props.nextSafetyScan.readinessReason ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                  data-testid="safety-case-scan-readiness-reason"
                >
                  {props.nextSafetyScan.readinessReason}
                </Typography>
              ) : null}
              {props.canManageSafetyCases && props.nextSafetyScan.showOpenSafetyScanPrimary && props.openSafetyScanHref ? (
                <Box sx={{ mt: 1.25 }}>
                  <Button
                    component={RouterLink}
                    to={props.openSafetyScanHref}
                    variant="contained"
                    color="primary"
                    data-testid="open-safety-scan-cta"
                    sx={{ fontWeight: 800, textTransform: 'none' }}
                  >
                    Open Safety Scan
                  </Button>
                </Box>
              ) : null}
            </Box>
          ) : null}

          {props.hasActiveDraft ? (
            <>
              <Typography variant="body2" sx={{ mt: 2.25 }} data-testid="safety-case-signed-scan-status">
                Safety Scan sign-off: {props.parsedLifecycle?.signoffReady ? 'Ready' : 'Not ready'}
                {props.parsedLifecycle ? (
                  <Box
                    component="span"
                    data-testid="safety-case-claim-readiness-for-this-scan"
                  >
                    {' · '}{props.parsedLifecycle.claimReadinessResolved}/{props.parsedLifecycle.claimReadinessTotal} checks for this scan
                  </Box>
                ) : (
                  <Box component="span" data-testid="safety-case-claim-readiness-for-this-scan">
                    {' · '}Readiness for this scan is loading…
                  </Box>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Signing is optional and only required for a claim-grade Safety Scan.
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ mt: 2.25 }} data-testid="safety-case-signed-scan-status">
              No current Safety Scan.
            </Typography>
          )}
          {props.canManageSafetyCases && props.showPrepareSignedScan && props.prepareSignedScanHref ? (
            <Button
              component={RouterLink}
              to={props.prepareSignedScanHref}
              variant="outlined"
              size="small"
              data-testid="prepare-signed-safety-scan"
              sx={{ mt: 1, ...secondaryActionSx }}
            >
              Prepare signed Safety Scan
            </Button>
          ) : null}
        </CardContent>
      </Card>
      )}

      {props.measurementReferences ? (
        <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid="safety-case-measurement-references">
          <CardContent sx={{ maxWidth: '46rem' }}>
            <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.12 }}>
              MEASUREMENT HISTORY
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Typography variant="body2">
                Initial baseline — {props.measurementReferences.initialBaseline.identity}
              </Typography>
              <Typography variant="body2">
                Current Control — {props.measurementReferences.controlInForceLabel}
              </Typography>
              <Typography variant="body2">
                Current Control reference — {props.measurementReferences.currentControlReference.identity}
              </Typography>
              <Typography variant="body2">
                Current Scan — {props.measurementReferences.currentScan.identity}
              </Typography>
              {props.measurementReferences.preControlBaselineUnavailable ? (
                <Typography variant="body2">
                  Historical Control result — NOT COMPARABLE
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Comparison: {props.measurementReferences.comparablePairLabel}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap">
              {props.canManageSafetyCases && props.measurementReferences.initialBaselineEligible && !props.measurementReferences.preControlBaselineUnavailable && props.onEstablishInitialBaseline ? (
                <Button size="small" variant="outlined" onClick={props.onEstablishInitialBaseline} data-testid="establish-initial-baseline" sx={secondaryActionSx}>
                  Establish Initial baseline
                </Button>
              ) : null}
              {props.canManageSafetyCases && props.measurementReferences.controlReferenceEligible && props.onEstablishControlReference ? (
                <Button size="small" variant="outlined" onClick={props.onEstablishControlReference} data-testid="establish-control-reference" sx={secondaryActionSx}>
                  Establish Current Control reference
                </Button>
              ) : null}
              <Button size="small" component={RouterLink} to={props.measurementReferences.historyHref} sx={secondaryActionSx}>
                View Scans / Records
              </Button>
              <Button size="small" component={RouterLink} to={props.overviewControlsHref} sx={secondaryActionSx}>
                View Controls
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid="safety-case-latest-scan-card">
        <CardContent sx={{ maxWidth: { xs: '100%', md: '46rem' } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Latest evidence</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }} data-testid="safety-case-scan-result-headline">
            {props.latestEvidence?.confirmedLine
              || (props.hasActiveDraft
                ? 'Loading scan review counts…'
                : (props.hasSignedScan ? 'Signed scan on file' : 'No Safety Scan is attached yet.'))}
          </Typography>
          {props.evidenceImageUrl ? (
            <Box
              data-testid="safety-case-latest-evidence-image"
              sx={{
                mt: 1.25,
                width: '100%',
                maxWidth: 320,
                aspectRatio: '16 / 9',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                component="img"
                src={props.evidenceImageUrl}
                alt={props.overviewPlace
                  ? `Representative evidence still for ${props.overviewPlace}`
                  : 'Representative evidence still'}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
          ) : null}
          {props.overviewPlace ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }} data-testid="safety-case-overview-place">
              {formatOverviewWhereThisHappens(props.overviewPlace)}
            </Typography>
          ) : null}
          {props.latestEvidence?.reviewLine ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {props.latestEvidence.reviewLine}
            </Typography>
          ) : null}
          {props.latestEvidence?.linkedLine ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {props.latestEvidence.linkedLine}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="safety-case-scan-window">
            {props.scanWindowLabel
              ? `Scan window: ${props.scanWindowLabel}`
              : (props.hasActiveDraft
                ? 'Scan window: Not recorded'
                : 'Current Scan window: No current Safety Scan')}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 1.5 }}
            data-testid="safety-case-scan-record-links"
            data-quiet={props.scanRecordsQuiet ? 'true' : 'false'}
          >
            {((props.canManageSafetyCases && props.hasActiveDraft) || props.hasSignedScan) ? (
              <Button
                variant="outlined"
                size="small"
                onClick={props.onViewSafetyScan}
                sx={secondaryActionSx}
              >
                View Safety Scan
              </Button>
            ) : null}
            {props.previousReportId && props.onViewPreviousSafetyScan ? (
              <Button
                variant="outlined"
                size="small"
                onClick={props.onViewPreviousSafetyScan}
                data-testid="safety-case-previous-scan-link"
                sx={secondaryActionSx}
              >
                Previous Safety Scan
              </Button>
            ) : null}
            {props.showReviewEvidence ? (
              <Button
                variant="outlined"
                size="small"
                onClick={props.onReviewEvidence}
                sx={secondaryActionSx}
              >
                Review evidence
              </Button>
            ) : null}
            {props.signedRecordHref ? (
              <Button
                component={RouterLink}
                to={props.signedRecordHref}
                variant="outlined"
                size="small"
                data-testid="safety-case-view-signed-record"
                sx={secondaryActionSx}
              >
                View signed record
              </Button>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          alignItems: 'stretch',
        }}
      >
      <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid="safety-case-overview-control">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>What the site did</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }} data-testid="safety-case-overview-control-title">
            {props.overviewControl.title}
          </Typography>
          {props.overviewControl.category ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }} data-testid="safety-case-overview-control-category">
              Category: {props.overviewControl.category}
            </Typography>
          ) : null}
          {props.overviewControl.detail ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {props.overviewControl.detail}
            </Typography>
          ) : null}
          {props.overviewControl.case === 'recorded' ? (
            <Stack spacing={0.25} sx={{ mt: 1 }} data-testid="safety-case-overview-control-meta">
              {props.overviewControl.owner ? (
                <Typography variant="body2" color="text.secondary">Control owner: {props.overviewControl.owner}</Typography>
              ) : null}
              {props.overviewControl.status ? (
                <Typography variant="body2" color="text.secondary">Status: {props.overviewControl.status}</Typography>
              ) : null}
              {props.overviewControl.date ? (
                <Typography variant="body2" color="text.secondary">
                  {props.overviewControl.dateKind === 'due' ? 'Due: ' : props.overviewControl.dateKind === 'recorded' ? 'Completed: ' : ''}{props.overviewControl.date}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
          {props.overviewControl.showViewUpdateLink ? (
            <Button
              component={RouterLink}
              to={props.overviewControlsHref}
              variant="outlined"
              size="small"
              data-testid="safety-case-overview-view-control"
              sx={{ mt: 1, ...secondaryActionSx }}
            >
              View / update control
            </Button>
          ) : null}
          {props.showRecordControlCta ? (
            <Button
              variant="contained"
              onClick={props.onRecordControl}
              data-testid="safety-case-overview-record-control"
              sx={{ mt: 1.5, fontWeight: 800 }}
            >
              Record control
            </Button>
          ) : null}
        </CardContent>
      </Card>
      <Card variant="outlined" sx={{ borderRadius: 3 }} id="safety-case-overview-followup" data-testid="safety-case-overview-followup">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {props.overviewFollowUp.kind === 'baseline_establishment' ? 'Baseline establishment' : 'Follow-up'}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }} data-testid="safety-case-overview-followup-headline">
            {props.overviewFollowUp.headline}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }} data-testid="safety-case-overview-followup-sentence">
            {props.overviewFollowUp.sentence}
          </Typography>
        </CardContent>
      </Card>
      </Box>
    </Stack>
  );
}
